import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, ShieldCheck, ShieldOff } from "lucide-react";
import { adminApi, errMsg } from "@/lib/api";
import { fmtMoney, fmtDate, TXN_LABELS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, EmptyState, useDebounced } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

function UserSheet({ id, onClose }) {
  const qc = useQueryClient();
  const { admin } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["admin-user", id], queryFn: () => adminApi.get(`/admin/users/${id}`).then((r) => r.data), enabled: !!id });
  const setRole = async (role) => {
    try {
      await adminApi.patch(`/admin/users/${id}/role`, { role });
      toast.success(`Role changed to ${role}`);
      qc.invalidateQueries({ queryKey: ["admin-user", id] });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="admin-user-sheet">
        <SheetHeader><SheetTitle>User details</SheetTitle></SheetHeader>
        {isLoading || !data ? <Skeleton className="mt-6 h-80" /> : (
          <div className="mt-4 space-y-6">
            <div>
              <p className="text-lg font-semibold">{data.user.name}</p>
              <p className="text-sm text-muted-foreground">{data.user.email}{data.user.phone ? ` · ${data.user.phone}` : ""}</p>
              <p className="mt-1 text-xs text-muted-foreground">Joined {fmtDate(data.user.created_at)} · role <span className="font-medium text-foreground">{data.user.role}</span></p>
              {data.user.id !== admin?.id && (
                data.user.role === "admin"
                  ? <Button size="sm" variant="outline" className="mt-3 gap-1.5" onClick={() => setRole("user")} data-testid="admin-user-revoke-admin"><ShieldOff className="h-4 w-4" /> Revoke admin</Button>
                  : <Button size="sm" variant="outline" className="mt-3 gap-1.5" onClick={() => setRole("admin")} data-testid="admin-user-make-admin"><ShieldCheck className="h-4 w-4" /> Make admin</Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {data.wallets.map((w) => <div key={w.id} className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">{w.currency} balance</p><p className="num mt-1 font-semibold" data-testid={`admin-user-balance-${w.currency.toLowerCase()}`}>{fmtMoney(w.balance, w.currency)}</p></div>)}
            </div>
            <div>
              <p className="mb-2 text-sm font-semibold">Deposits</p>
              {data.deposits.length === 0 ? <p className="text-sm text-muted-foreground">None</p> : (
                <ul className="divide-y rounded-lg border">{data.deposits.map((d) => (
                  <li key={d.id} className="flex items-center gap-2 px-3 py-2 text-sm"><span className="num text-xs">{d.id}</span><span className="num ml-auto">{fmtMoney(d.amount, d.currency)}</span><StatusBadge status={d.status} /></li>
                ))}</ul>
              )}
            </div>
            <div>
              <p className="mb-2 text-sm font-semibold">Ledger</p>
              {data.ledger.length === 0 ? <p className="text-sm text-muted-foreground">None</p> : (
                <ul className="divide-y rounded-lg border">{data.ledger.map((t) => (
                  <li key={t.id} className="flex items-center gap-2 px-3 py-2 text-sm"><span>{TXN_LABELS[t.transaction_type]}</span><span className="text-xs text-muted-foreground">{fmtDate(t.created_at)}</span><span className={`num ml-auto ${t.amount >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{t.amount >= 0 ? "+" : "−"}{fmtMoney(Math.abs(t.amount), t.currency)}</span></li>
                ))}</ul>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function AdminUsers() {
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const s = useDebounced(search, 350);
  const { data, isLoading } = useQuery({ queryKey: ["admin-users", s], queryFn: () => adminApi.get("/admin/users", { params: { search: s || undefined } }).then((r) => r.data) });
  return (
    <div>
      <PageHeader title="Users" subtitle="Registered users, balances and roles." />
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email or phone" className="h-10 bg-white pl-9" data-testid="admin-users-search-input" />
      </div>
      {isLoading ? <Skeleton className="h-80" /> : data.length === 0 ? <EmptyState title="No users found" /> : (
        <Card className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead className="text-right">INR</TableHead><TableHead className="text-right">USDT</TableHead><TableHead>Joined</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.map((u) => (
                <TableRow key={u.id} className="cursor-pointer hover:bg-muted/60" onClick={() => setOpenId(u.id)} data-testid="admin-user-row">
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                  <TableCell><span className={`rounded-md px-1.5 py-0.5 text-xs font-medium ${u.role === "admin" ? "bg-slate-900 text-white" : "bg-secondary"}`}>{u.role}</span></TableCell>
                  <TableCell className="num text-right">{fmtMoney(u.balances?.INR || 0, "INR")}</TableCell>
                  <TableCell className="num text-right">{fmtMoney(u.balances?.USDT || 0, "USDT", { noUnit: true })}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDate(u.created_at, false)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      <UserSheet id={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
